import React from 'react';
import { X, HelpCircle, Newspaper, PenSquare, Heart, MapPin, Settings, Shield, MessageCircle, ExternalLink } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import EditableTranslation from './EditableTranslation';

const HelpModal = ({ isOpen, onClose }) => {
  const { t } = useLanguage();
  
  if (!isOpen) return null;

  const helpTopics = [
    {
      icon: Newspaper,
      titleKey: 'help.browseNews',
      descKey: 'help.browseNewsDesc',
      color: 'text-blue-500'
    },
    {
      icon: PenSquare,
      titleKey: 'help.createArticles',
      descKey: 'help.createArticlesDesc',
      color: 'text-emerald-500'
    },
    {
      icon: Heart,
      titleKey: 'help.following',
      descKey: 'help.followingDesc',
      color: 'text-pink-500'
    },
    {
      icon: MapPin,
      titleKey: 'help.localNews',
      descKey: 'help.localNewsDesc',
      color: 'text-red-500'
    },
    {
      icon: MessageCircle,
      titleKey: 'help.comments',
      descKey: 'help.commentsDesc',
      color: 'text-purple-500'
    },
    {
      icon: Settings,
      titleKey: 'help.settings',
      descKey: 'help.settingsDesc',
      color: 'text-gray-500'
    },
    {
      icon: Shield,
      titleKey: 'help.adminPanel',
      descKey: 'help.adminPanelDesc',
      color: 'text-amber-500'
    },
    {
      icon: ExternalLink,
      titleKey: 'help.externalArticles',
      descKey: 'help.externalArticlesDesc',
      color: 'text-cyan-500'
    }
  ];

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div 
          className="bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-lg max-h-[85vh] overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 dark:border-gray-800">
            <div className="flex items-center gap-2">
              <HelpCircle className="h-5 w-5 text-emerald-500" />
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">
                <EditableTranslation translationKey="help.title">
                  {t('help.title')}
                </EditableTranslation>
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full"
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Content */}
          <div className="p-4 overflow-y-auto max-h-[calc(85vh-140px)]">
            <div className="text-center mb-6">
              <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                <EditableTranslation translationKey="help.welcomeTitle">
                  {t('help.welcomeTitle')}
                </EditableTranslation>
              </h3>
              <p className="text-gray-500 dark:text-gray-400 mt-1">
                <EditableTranslation translationKey="help.welcomeSubtitle">
                  {t('help.welcomeSubtitle')}
                </EditableTranslation>
              </p>
            </div>

            <div className="space-y-4">
              {helpTopics.map((topic, idx) => (
                <div 
                  key={idx}
                  className="flex gap-4 p-3 rounded-xl bg-gray-50 dark:bg-gray-800/50"
                >
                  <div className={`flex-shrink-0 ${topic.color}`}>
                    <topic.icon className="h-6 w-6" />
                  </div>
                  <div>
                    <h4 className="font-medium text-gray-900 dark:text-white">
                      <EditableTranslation translationKey={topic.titleKey}>
                        {t(topic.titleKey)}
                      </EditableTranslation>
                    </h4>
                    <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                      <EditableTranslation translationKey={topic.descKey} multiline>
                        {t(topic.descKey)}
                      </EditableTranslation>
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Quick Tips */}
            <div className="mt-6 p-4 bg-emerald-50 dark:bg-emerald-900/20 rounded-xl">
              <h4 className="font-medium text-emerald-800 dark:text-emerald-300 mb-2">
                <EditableTranslation translationKey="help.quickTips">
                  {t('help.quickTips')}
                </EditableTranslation>
              </h4>
              <ul className="text-sm text-emerald-700 dark:text-emerald-400 space-y-1">
                <li>• <EditableTranslation translationKey="help.tip1">{t('help.tip1')}</EditableTranslation></li>
                <li>• <EditableTranslation translationKey="help.tip2">{t('help.tip2')}</EditableTranslation></li>
                <li>• <EditableTranslation translationKey="help.tip3">{t('help.tip3')}</EditableTranslation></li>
                <li>• <EditableTranslation translationKey="help.tip4">{t('help.tip4')}</EditableTranslation></li>
              </ul>
            </div>
          </div>

          {/* Footer */}
          <div className="p-4 border-t border-gray-200 dark:border-gray-800">
            <button
              onClick={onClose}
              className="w-full py-2.5 bg-emerald-500 hover:bg-emerald-600 text-white font-medium rounded-lg"
            >
              <EditableTranslation translationKey="btn.gotIt">
                {t('btn.gotIt')}
              </EditableTranslation>
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default HelpModal;
